/** Dev/Preview timing telemetry for parallel Canvas bootstrap
 * (VISTOERA_OPENING_SPLASH_PREMIUM_BREATH_AND_PARALLEL_CANVAS_BOOTSTRAP_V2).
 *
 * Records high-resolution timestamps for splash vs canvas load overlap.
 * Logs a summary once splash ends; safe to import in production (no-op
 * overhead when disabled). */

export type CanvasBootstrapMark =
  | 'APP_BOOT_AT'
  | 'WORKSPACE_BOOTSTRAP_START_AT'
  | 'CANVAS_ROUTE_MOUNT_AT'
  | 'CANVAS_API_REQUEST_START_AT'
  | 'CANVAS_API_RESPONSE_AT'
  | 'CANVAS_NODE_HYDRATION_START_AT'
  | 'CANVAS_NODE_HYDRATION_COMPLETE_AT'
  | 'CANVAS_ASSET_REQUEST_START_AT'
  | 'CANVAS_FIRST_IMAGE_READY_AT'
  | 'CANVAS_FIRST_MEANINGFUL_NODE_READY_AT'
  | 'SPLASH_MOUNT_AT'
  | 'SPLASH_FADE_START_AT'
  | 'SPLASH_UNMOUNT_AT'

const marks = new Map<CanvasBootstrapMark, number>()
let summaryLogged = false

function telemetryEnabled(): boolean {
  if (typeof import.meta !== 'undefined' && import.meta.env.DEV) return true
  if (typeof import.meta !== 'undefined' && import.meta.env.VITE_CANVAS_BOOTSTRAP_TELEMETRY === '1') return true
  if (typeof window !== 'undefined' && window.location.hostname.includes('preview')) return true
  return false
}

export function markCanvasBootstrap(mark: CanvasBootstrapMark): void {
  if (!telemetryEnabled()) return
  if (!marks.has(mark)) {
    marks.set(mark, performance.now())
  }
}

function delta(from: CanvasBootstrapMark, to: CanvasBootstrapMark): number | null {
  const a = marks.get(from)
  const b = marks.get(to)
  if (a == null || b == null) return null
  return Math.round(b - a)
}

function beforeSplashEnd(mark: CanvasBootstrapMark): 'YES' | 'NO' | 'UNKNOWN' {
  const splashEnd = marks.get('SPLASH_UNMOUNT_AT')
  const t = marks.get(mark)
  if (splashEnd == null || t == null) return 'UNKNOWN'
  return t < splashEnd ? 'YES' : 'NO'
}

export function logCanvasBootstrapSummary(): void {
  if (!telemetryEnabled() || summaryLogged) return
  summaryLogged = true

  const splashStart = marks.get('SPLASH_MOUNT_AT')
  const fetchStart = marks.get('CANVAS_API_REQUEST_START_AT')
  const fetchDone = marks.get('CANVAS_API_RESPONSE_AT')
  const firstNode = marks.get('CANVAS_FIRST_MEANINGFUL_NODE_READY_AT')
  const firstImage = marks.get('CANVAS_FIRST_IMAGE_READY_AT')
  const splashEnd = marks.get('SPLASH_UNMOUNT_AT')

  const fetchMinusSplash =
    splashStart != null && fetchStart != null ? Math.round(fetchStart - splashStart) : null

  const report = {
    T_SPLASH_START: splashStart ?? null,
    T_CANVAS_FETCH_START: fetchStart ?? null,
    T_CANVAS_FETCH_DONE: fetchDone ?? null,
    T_FIRST_NODE_READY: firstNode ?? null,
    T_FIRST_IMAGE_READY: firstImage ?? null,
    T_SPLASH_END: splashEnd ?? null,
    CANVAS_FETCH_START_MINUS_SPLASH_START_MS: fetchMinusSplash,
    FIRST_NODE_READY_BEFORE_SPLASH_END: beforeSplashEnd('CANVAS_FIRST_MEANINGFUL_NODE_READY_AT'),
    FIRST_IMAGE_READY_BEFORE_SPLASH_END: beforeSplashEnd('CANVAS_FIRST_IMAGE_READY_AT'),
    CANVAS_ROUTE_TO_FETCH_MS: delta('CANVAS_ROUTE_MOUNT_AT', 'CANVAS_API_REQUEST_START_AT'),
    FETCH_DURATION_MS: delta('CANVAS_API_REQUEST_START_AT', 'CANVAS_API_RESPONSE_AT'),
    HYDRATION_MS: delta('CANVAS_NODE_HYDRATION_START_AT', 'CANVAS_NODE_HYDRATION_COMPLETE_AT'),
  }

  console.info('[CanvasBootstrapTelemetry]', JSON.stringify(report))
  if (typeof window !== 'undefined') {
    ;(window as unknown as { __CANVAS_BOOTSTRAP_REPORT__?: typeof report }).__CANVAS_BOOTSTRAP_REPORT__ =
      report
  }
}
